import type { MapManifest } from '../protocol/schemas.js';

export const BEACH_BATTLES_KOTH_MAP_ID = 'beach_battles_koth';
export const FREEPLAY_OPEN_WORLD_MAP_ID = 'freeplay_open_world';
export const SMOOTH_METRO_FREEPLAY_MAP_ID = 'smooth_metro_freeplay';
export const SMOOTH_COASTAL_DRIVE_MAP_ID = 'smooth_coastal_drive';
export const SMOOTH_ROYALE_HIGHLANDS_MAP_ID = 'smooth_royale_highlands';
export const SURVIVE_KOMODO_ISLAND_MAP_ID = 'survive_komodo_island';
export const HUB_MAP_ID = 'hub';
export const ADVENTURE_DUNGEON_MAP_ID = 'dungeon';
export const ARENA_PVP_MAP_ID = 'pvp_arena';
export const TEAM_ARENA_MAP_ID = 'team_arena';
export const BATTLE_ROYALE_ISLAND_MAP_ID = 'battle_royale';
export const BUILD_FIGHT_ARENA_MAP_ID = 'build_fight';
const FREEPLAY_SPAWN_Y = 35;
const METRO_PLAYABLE_HALF_SIZE = 1900;
const COASTAL_PLAYABLE_HALF_SIZE = 1080;
const ROYALE_PLAYABLE_HALF_SIZE = 1450;

const DEFAULT_MAP_BOUNDS = {
  minX: -128,
  maxX: 128,
  minY: 0,
  maxY: 256,
  minZ: -128,
  maxZ: 128,
} as const;

function makeFreePlaySpawnPoints(): MapManifest['spawnPoints'] {
  const spacing = 48;
  const gridRadius = 8;
  const spawns: MapManifest['spawnPoints'] = [
    { x: 0, y: FREEPLAY_SPAWN_Y, z: 0, yaw: 0 },
    { x: 32, y: FREEPLAY_SPAWN_Y, z: 0, yaw: -Math.PI / 2 },
    { x: -32, y: FREEPLAY_SPAWN_Y, z: 0, yaw: Math.PI / 2 },
    { x: 0, y: FREEPLAY_SPAWN_Y, z: 32, yaw: Math.PI },
    { x: 0, y: FREEPLAY_SPAWN_Y, z: -32, yaw: 0 },
  ];

  for (let gz = -gridRadius; gz <= gridRadius; gz += 1) {
    for (let gx = -gridRadius; gx <= gridRadius; gx += 1) {
      if (gx === 0 && gz === 0) {
        continue;
      }
      const yaw = Math.atan2(-gx, -gz);
      spawns.push({
        x: gx * spacing,
        y: FREEPLAY_SPAWN_Y,
        z: gz * spacing,
        yaw: Number.isFinite(yaw) ? yaw : 0,
      });
    }
  }

  return spawns;
}

function facingOriginYaw(x: number, z: number): number {
  const yaw = Math.atan2(-x, -z);
  return Number.isFinite(yaw) ? yaw : 0;
}

function spawnAt(x: number, z: number, yaw = facingOriginYaw(x, z)): MapManifest['spawnPoints'][number] {
  return { x, y: FREEPLAY_SPAWN_Y, z, yaw };
}

function dedupeSpawnPoints(spawns: readonly MapManifest['spawnPoints'][number][]): MapManifest['spawnPoints'] {
  const seen = new Set<string>();
  const result: MapManifest['spawnPoints'] = [];
  for (const spawn of spawns) {
    const key = `${Math.round(spawn.x * 10) / 10},${Math.round(spawn.z * 10) / 10}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(spawn);
  }
  return result;
}

function makeMetroSpawnPoints(): MapManifest['spawnPoints'] {
  const spawns: MapManifest['spawnPoints'] = [
    spawnAt(0, 0, 0),
    spawnAt(0, 34, Math.PI),
    spawnAt(34, 0, -Math.PI / 2),
    spawnAt(-34, 0, Math.PI / 2),
  ];

  const lanes = [-3, 0, 3] as const;
  for (let offset = -1280; offset <= 1280; offset += 40) {
    for (const lane of lanes) {
      spawns.push(spawnAt(lane, offset));
      spawns.push(spawnAt(offset, lane));
    }
  }

  for (let angleIndex = 0; angleIndex < 32; angleIndex += 1) {
    const angle = (angleIndex / 32) * Math.PI * 2;
    const radius = 172 + (angleIndex % 2) * 52;
    spawns.push(spawnAt(Math.cos(angle) * radius, Math.sin(angle) * radius, angle + Math.PI));
  }

  return dedupeSpawnPoints(spawns);
}

function makeCoastalSpawnPoints(): MapManifest['spawnPoints'] {
  const spawns: MapManifest['spawnPoints'] = [
    spawnAt(-700, 380, Math.PI / 2),
    spawnAt(-760, 390, Math.PI / 2),
    spawnAt(-640, 390, -Math.PI / 2),
    spawnAt(-700, 320, 0),
    spawnAt(-700, 460, Math.PI),
  ];

  for (let x = -1040; x <= -420; x += 44) {
    spawns.push(spawnAt(x, 386, Math.PI / 2));
    spawns.push(spawnAt(x, 414, Math.PI / 2));
  }
  for (let z = 230; z <= 560; z += 42) {
    spawns.push(spawnAt(-716, z, 0));
    spawns.push(spawnAt(-684, z, Math.PI));
  }
  for (let x = -960; x <= 960; x += 96) {
    spawns.push(spawnAt(x, 720 + Math.sin(x * 0.0022) * 120, x < 0 ? Math.PI / 2 : -Math.PI / 2));
  }

  return dedupeSpawnPoints(spawns);
}

function makeRoyaleHighlandsSpawnPoints(): MapManifest['spawnPoints'] {
  const spawns: MapManifest['spawnPoints'] = [
    spawnAt(0, 0, 0),
    spawnAt(0, 96, Math.PI),
    spawnAt(96, 0, -Math.PI / 2),
    spawnAt(-96, 0, Math.PI / 2),
  ];

  for (let offset = -1160; offset <= 1160; offset += 96) {
    spawns.push(spawnAt(0, offset));
    spawns.push(spawnAt(offset, 0));
  }
  for (let angleIndex = 0; angleIndex < 40; angleIndex += 1) {
    const angle = ((angleIndex + 0.5) / 40) * Math.PI * 2;
    const radius = 620 + (angleIndex % 2) * 80;
    spawns.push(spawnAt(Math.cos(angle) * radius, Math.sin(angle) * radius, angle + Math.PI));
  }

  return dedupeSpawnPoints(spawns);
}

function makeKomodoIslandSpawnPoints(): MapManifest['spawnPoints'] {
  return [
    { x: -64, y: 70, z: 232, yaw: 1.570796 },
    { x: 264, y: 69, z: 160, yaw: 1.570796 },
    { x: 256, y: 69, z: 240, yaw: 1.570796 },
    { x: 208, y: 69, z: 200, yaw: 1.570796 },
    { x: 216, y: 69, z: 248, yaw: 1.570796 },
    { x: 312, y: 69, z: 96, yaw: 1.570796 },
    { x: 216, y: 69, z: 88, yaw: 1.570796 },
    { x: 144, y: 69, z: 240, yaw: 1.570796 },
    { x: 144, y: 69, z: 120, yaw: 1.570796 },
    { x: 112, y: 69, z: 256, yaw: 1.570796 },
    { x: 232, y: 69, z: 8, yaw: 1.570796 },
    { x: 336, y: 69, z: 16, yaw: 1.570796 },
    { x: 56, y: 69, z: 256, yaw: 1.570796 },
    { x: 280, y: 69, z: -16, yaw: 1.570796 },
    { x: 200, y: 69, z: 408, yaw: 1.570796 },
    { x: 312, y: 69, z: -16, yaw: 1.570796 },
    { x: 240, y: 70, z: -32, yaw: 1.570796 },
    { x: 272, y: 69, z: -48, yaw: 1.570796 },
    { x: 304, y: 69, z: -48, yaw: 1.570796 },
    { x: 32, y: 69, z: 304, yaw: 1.570796 },
    { x: 8, y: 77, z: 224, yaw: 1.570796 },
    { x: 232, y: 69, z: -64, yaw: 1.570796 },
    { x: 16, y: 78, z: 352, yaw: 1.570796 },
    { x: -32, y: 69, z: 256, yaw: 1.570796 },
    { x: 336, y: 69, z: -96, yaw: 1.570796 },
    { x: -24, y: 69, z: 304, yaw: 1.570796 },
    { x: -64, y: 70, z: 232, yaw: 1.570796 },
    { x: 32, y: 69, z: 448, yaw: 1.570796 },
    { x: -80, y: 69, z: 296, yaw: 1.570796 },
    { x: 0, y: 69, z: 456, yaw: 1.570796 },
    { x: 256, y: 82, z: -176, yaw: 1.570796 },
    { x: -32, y: 69, z: 464, yaw: 1.570796 },
  ];
}

const BEACH_BATTLES_KOTH: MapManifest = {
  mapId: BEACH_BATTLES_KOTH_MAP_ID,
  name: 'Beach Battles KoTH',
  bounds: {
    minX: -128,
    maxX: 143,
    minY: 0,
    maxY: 127,
    minZ: -128,
    maxZ: 143,
  },
  spawnPoints: [
    { x: 40, y: 16, z: 28, yaw: 0 },
    { x: -42, y: 16, z: 28, yaw: 0 },
    { x: 40, y: 16, z: 10, yaw: 3.14 },
    { x: -42, y: 16, z: 10, yaw: 3.14 },
    { x: 52, y: 22, z: 42, yaw: 0 },
    { x: 52, y: 22, z: 26, yaw: 3.14 },
    { x: 40, y: 16, z: -10, yaw: 0 },
    { x: -42, y: 16, z: -10, yaw: 0 },
    { x: 0, y: 58, z: 102, yaw: 0 },
  ],
  assets: {},
};

const FREEPLAY_OPEN_WORLD: MapManifest = {
  mapId: FREEPLAY_OPEN_WORLD_MAP_ID,
  name: 'Free Play Open World',
  bounds: {
    minX: -4096,
    maxX: 4096,
    minY: 0,
    maxY: 255,
    minZ: -4096,
    maxZ: 4096,
  },
  spawnPoints: makeFreePlaySpawnPoints(),
  assets: {},
};

const SMOOTH_METRO_FREEPLAY: MapManifest = {
  mapId: SMOOTH_METRO_FREEPLAY_MAP_ID,
  name: 'Freeplay Island City',
  bounds: {
    minX: -METRO_PLAYABLE_HALF_SIZE,
    maxX: METRO_PLAYABLE_HALF_SIZE,
    minY: 0,
    maxY: 255,
    minZ: -METRO_PLAYABLE_HALF_SIZE,
    maxZ: METRO_PLAYABLE_HALF_SIZE,
  },
  spawnPoints: makeMetroSpawnPoints(),
  assets: {
    surfacePreset: 'metro',
  },
};

const SMOOTH_COASTAL_DRIVE: MapManifest = {
  mapId: SMOOTH_COASTAL_DRIVE_MAP_ID,
  name: 'Smooth Coastal Drive',
  bounds: {
    minX: -COASTAL_PLAYABLE_HALF_SIZE,
    maxX: COASTAL_PLAYABLE_HALF_SIZE,
    minY: 0,
    maxY: 255,
    minZ: -COASTAL_PLAYABLE_HALF_SIZE,
    maxZ: COASTAL_PLAYABLE_HALF_SIZE,
  },
  spawnPoints: makeCoastalSpawnPoints(),
  assets: {
    surfacePreset: 'coastal_drive',
  },
};

const SMOOTH_ROYALE_HIGHLANDS: MapManifest = {
  mapId: SMOOTH_ROYALE_HIGHLANDS_MAP_ID,
  name: 'Smooth Royale Highlands',
  bounds: {
    minX: -ROYALE_PLAYABLE_HALF_SIZE,
    maxX: ROYALE_PLAYABLE_HALF_SIZE,
    minY: 0,
    maxY: 255,
    minZ: -ROYALE_PLAYABLE_HALF_SIZE,
    maxZ: ROYALE_PLAYABLE_HALF_SIZE,
  },
  spawnPoints: makeRoyaleHighlandsSpawnPoints(),
  assets: {
    surfacePreset: 'royale_highlands',
  },
};

const SURVIVE_KOMODO_ISLAND: MapManifest = {
  mapId: SURVIVE_KOMODO_ISLAND_MAP_ID,
  name: 'Survive Komodo Island',
  bounds: {
    minX: -352,
    maxX: 431,
    minY: 0,
    maxY: 255,
    minZ: -256,
    maxZ: 527,
  },
  spawnPoints: makeKomodoIslandSpawnPoints(),
  assets: {
    world: '/maps/worlds/survive-komodo-island.voxelmap.json',
  },
};

const HUB: MapManifest = {
  mapId: HUB_MAP_ID,
  name: 'Portal Hub',
  bounds: { minX: 0, maxX: 63, minY: 0, maxY: 31, minZ: 0, maxZ: 63 },
  spawnPoints: [
    { x: 22, y: 6, z: 22, yaw: Math.PI / 4 },
    { x: 42, y: 6, z: 22, yaw: -Math.PI / 4 },
    { x: 22, y: 6, z: 42, yaw: Math.PI * 0.75 },
    { x: 42, y: 6, z: 42, yaw: -Math.PI * 0.75 },
  ],
  assets: {},
};

const ADVENTURE_DUNGEON: MapManifest = {
  mapId: ADVENTURE_DUNGEON_MAP_ID,
  name: 'Dungeon Run',
  bounds: { minX: 0, maxX: 47, minY: 0, maxY: 19, minZ: 0, maxZ: 47 },
  spawnPoints: [{ x: 4, y: 2, z: 8, yaw: Math.PI / 2 }],
  assets: {},
};

const ARENA_PVP: MapManifest = {
  mapId: ARENA_PVP_MAP_ID,
  name: 'Arena Blocks',
  bounds: { minX: 0, maxX: 63, minY: 0, maxY: 31, minZ: 0, maxZ: 63 },
  spawnPoints: [
    { x: 3, y: 6, z: 3, yaw: Math.PI / 4 },
    { x: 60, y: 6, z: 3, yaw: -Math.PI / 4 },
    { x: 3, y: 6, z: 60, yaw: Math.PI * 0.75 },
    { x: 60, y: 6, z: 60, yaw: -Math.PI * 0.75 },
  ],
  assets: {},
};

const TEAM_ARENA: MapManifest = {
  mapId: TEAM_ARENA_MAP_ID,
  name: 'Team Control Arena',
  bounds: { minX: 0, maxX: 79, minY: 0, maxY: 31, minZ: 0, maxZ: 79 },
  spawnPoints: [
    { x: 7, y: 6, z: 7, yaw: Math.PI / 4 },
    { x: 72, y: 6, z: 72, yaw: -Math.PI * 0.75 },
  ],
  assets: {},
};

const BATTLE_ROYALE_ISLAND: MapManifest = {
  mapId: BATTLE_ROYALE_ISLAND_MAP_ID,
  name: 'Royale Island',
  bounds: { minX: 0, maxX: 119, minY: 0, maxY: 63, minZ: 0, maxZ: 119 },
  spawnPoints: [
    { x: 40, y: 42, z: 40, yaw: Math.PI / 4 },
    { x: 80, y: 42, z: 80, yaw: -Math.PI * 0.75 },
    { x: 40, y: 42, z: 80, yaw: Math.PI * 0.75 },
    { x: 80, y: 42, z: 40, yaw: -Math.PI / 4 },
  ],
  assets: {},
};

const BUILD_FIGHT_ARENA: MapManifest = {
  mapId: BUILD_FIGHT_ARENA_MAP_ID,
  name: 'Build Fight Box',
  bounds: { minX: 0, maxX: 63, minY: 0, maxY: 47, minZ: 0, maxZ: 63 },
  spawnPoints: [
    { x: 10, y: 6, z: 10, yaw: Math.PI / 4 },
    { x: 53, y: 6, z: 53, yaw: -Math.PI * 0.75 },
  ],
  assets: {},
};

const MAP_MANIFESTS: Readonly<Record<string, MapManifest>> = {
  [SURVIVE_KOMODO_ISLAND_MAP_ID]: SURVIVE_KOMODO_ISLAND,
  [SMOOTH_METRO_FREEPLAY_MAP_ID]: SMOOTH_METRO_FREEPLAY,
  [SMOOTH_COASTAL_DRIVE_MAP_ID]: SMOOTH_COASTAL_DRIVE,
  [SMOOTH_ROYALE_HIGHLANDS_MAP_ID]: SMOOTH_ROYALE_HIGHLANDS,
  [FREEPLAY_OPEN_WORLD_MAP_ID]: FREEPLAY_OPEN_WORLD,
  [HUB_MAP_ID]: HUB,
  [ADVENTURE_DUNGEON_MAP_ID]: ADVENTURE_DUNGEON,
  [ARENA_PVP_MAP_ID]: ARENA_PVP,
  [TEAM_ARENA_MAP_ID]: TEAM_ARENA,
  [BATTLE_ROYALE_ISLAND_MAP_ID]: BATTLE_ROYALE_ISLAND,
  [BUILD_FIGHT_ARENA_MAP_ID]: BUILD_FIGHT_ARENA,
  freeplay: FREEPLAY_OPEN_WORLD,
  'freeplay-open-world': FREEPLAY_OPEN_WORLD,
  metro: SMOOTH_METRO_FREEPLAY,
  'smooth-metro': SMOOTH_METRO_FREEPLAY,
  'smooth-freeplay': SMOOTH_METRO_FREEPLAY,
  coast: SMOOTH_COASTAL_DRIVE,
  coastal: SMOOTH_COASTAL_DRIVE,
  driving: SMOOTH_COASTAL_DRIVE,
  highlands: SMOOTH_ROYALE_HIGHLANDS,
  royale: SMOOTH_ROYALE_HIGHLANDS,
  beach_battles_koth: BEACH_BATTLES_KOTH,
  'beach-battles-koth': BEACH_BATTLES_KOTH,
  'beach-battles': BEACH_BATTLES_KOTH,
  'survive-komodo-island': SURVIVE_KOMODO_ISLAND,
  komodo: SURVIVE_KOMODO_ISLAND,
  'komodo-island': SURVIVE_KOMODO_ISLAND,
};

export function isProceduralOpenWorldMapId(mapId: string | undefined): boolean {
  return mapId === undefined ||
    mapId === 'default' ||
    mapId === FREEPLAY_OPEN_WORLD_MAP_ID ||
    mapId === SMOOTH_METRO_FREEPLAY_MAP_ID ||
    mapId === SMOOTH_COASTAL_DRIVE_MAP_ID ||
    mapId === SMOOTH_ROYALE_HIGHLANDS_MAP_ID;
}

export type OpenWorldSurfacePresetId = 'metro' | 'coastal_drive' | 'royale_highlands';

export function openWorldSurfacePresetForMapId(mapId: string | undefined): OpenWorldSurfacePresetId | null {
  switch (mapId) {
    case FREEPLAY_OPEN_WORLD_MAP_ID:
    case SMOOTH_METRO_FREEPLAY_MAP_ID:
    case 'metro':
    case 'smooth-metro':
    case 'smooth-freeplay':
      return 'metro';
    case SMOOTH_COASTAL_DRIVE_MAP_ID:
    case 'coast':
    case 'coastal':
    case 'driving':
      return 'coastal_drive';
    case SMOOTH_ROYALE_HIGHLANDS_MAP_ID:
    case 'highlands':
    case 'royale':
      return 'royale_highlands';
    default:
      return null;
  }
}

function cloneManifest(manifest: MapManifest, mapId: string): MapManifest {
  return {
    ...manifest,
    mapId,
    bounds: { ...manifest.bounds },
    spawnPoints: manifest.spawnPoints.map((spawn) => ({ ...spawn })),
    assets: { ...manifest.assets },
  };
}

export function mapManifestFor(mapId: string): MapManifest {
  const manifest = MAP_MANIFESTS[mapId];
  if (manifest !== undefined) {
    return cloneManifest(manifest, mapId);
  }

  return {
    mapId,
    name: 'Default World',
    bounds: { ...DEFAULT_MAP_BOUNDS },
    spawnPoints: [{ x: 0, y: 80, z: 0, yaw: 0 }],
    assets: {},
  };
}
